import { useApolloClient, useMutation, useQuery } from "@apollo/client"
import { Dialog } from "@headlessui/react"
import { type UppyFile } from "@uppy/core"
import type Uppy from "@uppy/core"
import { FileInput } from "@uppy/react"
import { motion, AnimatePresence } from "framer-motion"
import { debounce } from "lodash-es"
import React, { useCallback, useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { useSelector, useDispatch } from "react-redux"
import invariant from "tiny-invariant"
import { graphql } from "@/api"
import { type OrderFontsQuery, type OrderFontsQueryVariables } from "@/api/graphql"
import LoadingAnimation from "@/common/components/loading"
import env from "@/common/env"
import { useUppy } from "@/common/hooks/use_uppy"
import { transitionTime } from "@/common/util"
import type Sign from "@/signomatic/app/sign"
import { actions } from "@/signomatic/store/actions"
import { type State } from "@/signomatic/store/reducers"
import Fonts, { type Fontdata, type FontVariant } from "@/signomatic/util/fonts"
import { CloseLarge, ArrowRight } from "@/skylticons"

const fontUploadMutation = graphql(`
  mutation FontUpload($orderId: ID!, $adminId: ID!, $filename: String!, $fileData: JSON!) {
    fontUpload(orderId: $orderId, adminId: $adminId, filename: $filename, fileData: $fileData) {
      font {
        id
        filename
        basename
        type
        path
      }
    }
  }
`)

const fontDeleteMutation = graphql(`
  mutation FontDelete($id: ID!) {
    fontDelete(id: $id) {
      font {
        id
      }
    }
  }
`)

const orderFontsDoc = graphql(`
  query OrderFonts($orderId: ID!) {
    orderFonts(orderId: $orderId) {
      id
      filename
      basename
      type
      path
    }
  }
`)

interface WrapperProps {
  open: boolean
  handleClose?: (e?: React.MouseEvent) => void
  children: React.ReactNode
}

const Wrapper: React.FC<WrapperProps> = ({ open, handleClose, children }) => {
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-40 bg-ink-100 bg-opacity-60"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: transitionTime(0.2), ease: "easeInOut" }}
            onClick={handleClose}
          ></motion.div>

          <Dialog
            open={open}
            as="div"
            className="font-upload-modal fixed inset-0 z-50 m-auto h-full max-h-[520px] w-full max-w-[840px]"
            onClose={() => handleClose}
            onClick={handleClose}
          >
            <motion.div
              className="h-full w-full p-4 sm:p-12"
              initial={{ translateY: "100vh" }}
              animate={{ translateY: 0 }}
              exit={{ translateY: "100vh" }}
              transition={{ duration: transitionTime(0.2), ease: "easeInOut" }}
            >
              <div className="font-upload-dialog relative flex h-fit w-full flex-col rounded-xl bg-white-1">
                {children}
              </div>
            </motion.div>
          </Dialog>
        </>
      )}
    </AnimatePresence>
  )
}

interface FontUploadModalProps {
  closeModal: (e?: React.MouseEvent) => void
  open: boolean
  uppy: Uppy
  uploadedFont: string
  uploadInProgress: boolean
  fontLoadEnabled: boolean
  handleCustomFont: () => void
  uploadError: boolean
  handlingError: boolean
  alreadyUploaded: boolean
  setAlreadyUploaded: (uploaded: boolean) => void
  deleteUnhandledFont: () => void
}

const maxNumberOfFiles = 1
const allowedFileTypes = [".woff", ".ttf", ".otf"]

const FontUploadModal: React.FC<FontUploadModalProps> = ({
  open,
  closeModal,
  uppy,
  uploadedFont,
  uploadInProgress,
  fontLoadEnabled,
  handleCustomFont,
  uploadError,
  handlingError,
  alreadyUploaded,
  setAlreadyUploaded,
  deleteUnhandledFont,
}) => {
  const handleClose = useCallback(
    (e?: React.MouseEvent) => {
      closeModal(e)
      return
    },
    [closeModal]
  )

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeModal()
        return
      }
    }

    document.addEventListener("keydown", handler)

    return () => document.removeEventListener("keydown", handler)
  }, [closeModal])

  return (
    <Wrapper open={open} handleClose={handleClose}>
      <div className="rounded-xl bg-white-1 p-10">
        <div className="header mb-6 flex items-center">
          <h3 className="heading-three m-0 pr-12">Custom font upload</h3>
          <button
            className="font-upload-close button button-large button-subtle icon-only no-focus absolute right-8 top-6 mb-0"
            onClick={() => handleClose()}
            type="button"
          >
            <CloseLarge />
          </button>
        </div>
        <div>
          <p className="paragraph">
            Select a font file to use. Make sure to check the file with an anti-virus software before uploading. The
            file can be in <b>.woff</b>, <b>.ttf</b>, or <b>.otf</b> format.
          </p>
          <div className="mb-6 flex flex-col items-start sm:flex-row sm:items-center">
            <div
              onClick={debounce(() => {
                setAlreadyUploaded(false)
                const files = uppy.getFiles()

                for (const file of files) {
                  uppy.removeFile(file.id)
                }

                deleteUnhandledFont()
              }, 300)}
            >
              <FileInput
                uppy={uppy}
                inputName="file"
                locale={{
                  strings: {
                    chooseFiles: "Choose file",
                  },
                }}
              />
            </div>

            {uploadError || alreadyUploaded ? (
              alreadyUploaded ? (
                <span className="ml-2 text-lg text-info">
                  Font with the same filename has been already uploaded to this order.
                </span>
              ) : (
                <span className="ml-2 text-lg text-error">
                  There was a problem uploading the file. Please try again.
                </span>
              )
            ) : (
              <span className="mt-2 text-lg sm:ml-2 sm:mt-0">
                {uploadedFont !== "" ? uploadedFont : "No file has been selected"}
              </span>
            )}
          </div>
          <div className="flex flex-col items-start sm:flex-row sm:items-center">
            <button
              className="load-file button button-primary icon-right mb-0"
              type="button"
              disabled={!fontLoadEnabled}
              onClick={() => handleCustomFont()}
            >
              Load file <ArrowRight />
            </button>
            {uploadInProgress && <LoadingAnimation className="ml-2" />}
            {handlingError && (
              <span className="mt-2 text-lg text-error sm:ml-2 sm:mt-0">
                Problem with reading the file. Please check that it is not damaged and try again.
              </span>
            )}
          </div>
        </div>
      </div>
    </Wrapper>
  )
}

interface FontUploadProps {
  sign: Sign
  orderId: string
  adminId: string
  fontdata: Fontdata
}

const FontUpload: React.FC<FontUploadProps> = ({ sign, orderId, adminId, fontdata }) => {
  const fontUploadOpen = useSelector<State, boolean>(({ fontUploadOpen }) => fontUploadOpen)
  const dispatch = useDispatch()

  const client = useApolloClient()
  const [uploadFont] = useMutation(fontUploadMutation)
  const [deleteFont] = useMutation(fontDeleteMutation)
  const [uploadedFont, setUploadedFont] = useState("")
  const [uploadError, setUploadError] = useState(false)
  const [handlingError, setHandlingError] = useState(false)
  const [fontLoadEnabled, setFontLoadEnabled] = useState(false)
  const [uploadInProgress, setUploadInProgress] = useState(false)
  const [alreadyUploaded, setAlreadyUploaded] = useState(false)

  const results = useQuery(orderFontsDoc, { variables: { orderId: orderId } })

  const { uppy } = useUppy("uploader", allowedFileTypes, true, maxNumberOfFiles)

  const removeFontFromFontdata = useCallback(
    (font: string) => {
      const fontdataCopy = fontdata
      const setKeys = ["all", "limited", "cutter", "contour", "tactile", "custom"]

      delete fontdataCopy.filemap[font.toLowerCase()]

      setKeys.forEach(key => {
        invariant(fontdataCopy.sets[key])
        fontdataCopy.sets[key].pop()
      })

      sign.updateFontdata(fontdataCopy)
    },
    [fontdata, sign]
  )

  const deleteUnhandledFont = useCallback(() => {
    const orderFonts = results.data?.orderFonts || []

    if (orderFonts.length === 0) {
      return
    }

    const lastUploadedFont = orderFonts[orderFonts.length - 1]
    invariant(lastUploadedFont)

    if (handlingError || !fontdata.sets.all.includes(lastUploadedFont.basename)) {
      deleteFont({
        variables: { id: lastUploadedFont.id },
        onCompleted: data => {
          const deletedFont = data.fontDelete?.font

          if (deletedFont && results.data?.orderFonts) {
            const updatedFonts = results.data.orderFonts.filter(font => font.id !== deletedFont.id)

            client.writeQuery<OrderFontsQuery, OrderFontsQueryVariables>({
              query: orderFontsDoc,
              variables: { orderId },
              data: { orderFonts: updatedFonts },
            })
          }

          if (handlingError) {
            removeFontFromFontdata(lastUploadedFont.basename)
          }

          setHandlingError(false)
        },
      })
    }
  }, [deleteFont, handlingError, results, removeFontFromFontdata, fontdata.sets.all, client, orderId])

  const closeModal = useCallback(
    (e?: React.MouseEvent) => {
      if (e?.target instanceof Element && e.target.closest(".font-upload-dialog")) {
        return
      }

      const files = uppy.getFiles()

      for (const file of files) {
        uppy.removeFile(file.id)
      }

      deleteUnhandledFont()
      setAlreadyUploaded(false)
      setUploadedFont("")
      setFontLoadEnabled(false)

      dispatch(actions.closeFontUpload())
    },
    [dispatch, setUploadedFont, deleteUnhandledFont, uppy]
  )

  useEffect(() => {
    sign.updateFontdata(fontdata)
  }, [sign, fontdata])

  useEffect(() => {
    const success = (uploaded: UppyFile, response: any) => {
      if (!uploaded) {
        return
      }

      const orderFonts = (results.data?.orderFonts || []).map((font: FontVariant) => font.filename)

      if (orderFonts.includes(uploaded.name)) {
        setAlreadyUploaded(true)
        setUploadInProgress(false)

        return
      }

      const uploadedFileData = {
        id: env.TEST ? response.body?.uploadURL : response.uploadURL,
        storage: "cache",
        metadata: {
          size: uploaded.size,
          filename: uploaded.name,
          mime_type: uploaded.type,
        },
      }

      uploadFont({
        variables: { orderId, adminId, filename: uploaded.name, fileData: uploadedFileData },
        onCompleted: data => {
          setUploadInProgress(false)
          setUploadedFont(uploaded.name)
          setFontLoadEnabled(true)

          const uploadedFont = data.fontUpload?.font

          if (uploadedFont && results.data?.orderFonts) {
            client.writeQuery<OrderFontsQuery, OrderFontsQueryVariables>({
              query: orderFontsDoc,
              variables: { orderId },
              data: { orderFonts: [...results.data.orderFonts, uploadedFont] },
            })
          }
        },
        onError: () => setUploadError(true),
      })
    }

    const uploadStarted = () => setUploadInProgress(true)

    uppy.on("upload", uploadStarted)
    uppy.on("upload-success", success)

    return () => {
      uppy.off("upload", uploadStarted)
      uppy.off("upload-success", success)
    }
  }, [results, uploadFont, uppy, adminId, orderId, client])

  const handleCustomFont = useCallback(() => {
    const orderFonts = results.data?.orderFonts || []
    const uploadedFont = orderFonts[orderFonts.length - 1]
    invariant(uploadedFont)
    const fontdataCopy = fontdata
    const setKeys = ["all", "limited", "cutter", "contour", "tactile", "custom"]

    fontdataCopy.filemap[uploadedFont.basename.toLowerCase()] = {
      normal: {
        path: uploadedFont.path,
        filename: uploadedFont.filename,
        basename: uploadedFont.basename,
        type: uploadedFont.type,
      },
    }

    setKeys.forEach(key => {
      invariant(fontdataCopy.sets[key])
      fontdataCopy.sets[key].push(uploadedFont.basename)
    })

    Fonts.loadFont(uploadedFont.basename.toLowerCase(), fontdataCopy)
      .then(() => {
        sign.updateFontdata(fontdataCopy)
        closeModal()
        setFontLoadEnabled(false)
      })
      .catch(error => {
        setHandlingError(true)
        console.error(error)
      })
  }, [closeModal, sign, fontdata, results.data?.orderFonts])

  if (!fontUploadOpen) {
    return null
  }

  return createPortal(
    <FontUploadModal
      open={fontUploadOpen}
      closeModal={closeModal}
      uppy={uppy}
      uploadedFont={uploadedFont}
      uploadInProgress={uploadInProgress}
      fontLoadEnabled={fontLoadEnabled}
      handleCustomFont={() => handleCustomFont()}
      uploadError={uploadError}
      handlingError={handlingError}
      alreadyUploaded={alreadyUploaded}
      setAlreadyUploaded={uploaded => setAlreadyUploaded(uploaded)}
      deleteUnhandledFont={() => deleteUnhandledFont()}
    />,
    document.body
  )
}

export default FontUpload
